import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SimulatorProvider } from "@/context/SimulatorContext";
import { Header } from "@/components/simulator/Header";
import { OverviewTab } from "@/components/simulator/OverviewTab";
import { SaffronTab } from "@/components/simulator/SaffronTab";
import { AlgaeTab } from "@/components/simulator/AlgaeTab";
import { OpexTab } from "@/components/simulator/OpexTab";
import { ProjectionsTab } from "@/components/simulator/ProjectionsTab";
import { ScenariosTab } from "@/components/simulator/ScenariosTab";
import { OptimizerTab } from "@/components/simulator/OptimizerTab";
import { FutureLotsTab } from "@/components/simulator/FutureLotsTab";
import { NewLotsTab } from "@/components/simulator/NewLotsTab";
import { EnergyTab } from "@/components/simulator/EnergyTab";
import { PrintReport } from "@/components/simulator/PrintReport";
import { Toaster } from "@/components/ui/sonner";
import {
  LayoutDashboard,
  Flower2,
  Microscope,
  Receipt,
  TrendingUp,
  FolderKanban,
  Target,
  Layers,
  Beaker,
  Sun,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Riepilogo", icon: LayoutDashboard },
  { id: "lot1-saffron", label: "Zafferano", icon: Flower2 },
  { id: "lot2-algae", label: "Microalghe", icon: Microscope },
  { id: "new-lots", label: "Lotti 3-5", icon: Beaker },
  { id: "opex", label: "Costi OPEX", icon: Receipt },
  { id: "energy", label: "Energia & H₂", icon: Sun },
  { id: "projections", label: "Proiezione 5Y", icon: TrendingUp },
  { id: "optimizer", label: "Optimizer", icon: Target },
  { id: "future-lots", label: "Lotti Futuri", icon: Layers },
  { id: "scenarios", label: "Scenari", icon: FolderKanban },
];

export default function SimulatorPage() {
  return (
    <SimulatorProvider>
      <div className="min-h-screen grain-bg bg-[#070a10]">
        <Header />

        <main className="no-print max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50 tracking-tight">
              Simulatore Economico
            </h1>
            <p className="text-sm text-slate-400 mt-1.5 max-w-3xl">
              Simulazione live della produzione, dei ricavi e della crescita di
              H2 Farm su due lotti produttivi. Ogni parametro è modificabile —
              i totali si aggiornano in tempo reale.
            </p>
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-slate-900/60 border border-slate-800 p-1 h-auto flex-wrap gap-1 w-full justify-start">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <TabsTrigger
                    key={t.id}
                    value={t.id}
                    data-testid={`tab-${t.id}`}
                    className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 data-[state=active]:border-emerald-500/40 border border-transparent text-slate-400 hover:text-slate-100 text-sm px-3.5 py-1.5"
                  >
                    <Icon className="h-3.5 w-3.5 mr-1.5" />
                    <span>{t.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent value="overview" className="mt-6"><OverviewTab /></TabsContent>
            <TabsContent value="lot1-saffron" className="mt-6"><SaffronTab /></TabsContent>
            <TabsContent value="lot2-algae" className="mt-6"><AlgaeTab /></TabsContent>
            <TabsContent value="new-lots" className="mt-6"><NewLotsTab /></TabsContent>
            <TabsContent value="opex" className="mt-6"><OpexTab /></TabsContent>
            <TabsContent value="energy" className="mt-6"><EnergyTab /></TabsContent>
            <TabsContent value="projections" className="mt-6"><ProjectionsTab /></TabsContent>
            <TabsContent value="optimizer" className="mt-6"><OptimizerTab /></TabsContent>
            <TabsContent value="future-lots" className="mt-6"><FutureLotsTab /></TabsContent>
            <TabsContent value="scenarios" className="mt-6"><ScenariosTab /></TabsContent>
          </Tabs>

          <footer className="mt-16 pt-6 border-t border-slate-800/60 text-center text-xs text-slate-600">
            H2 Farm · Simulatore Economico · Presentazione Investor · v1.0
          </footer>
        </main>

        {/* Hidden print-only report */}
        <PrintReport />

        <Toaster
          position="top-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "rgba(15, 23, 42, 0.95)",
              border: "1px solid #1f2937",
              color: "#f9fafb",
            },
          }}
        />
      </div>
    </SimulatorProvider>
  );
}
